import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useContext } from "react";
import { displayAccountLabel } from "@common/helpers/accounting";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useAccounts } from "@hooks/useAccounts";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import itemCellStyles from "@components/invoices/views/invoiceLineItemsView/cells/ItemCell.module.scss";
import descriptionCellStyles from "@components/invoices/views/invoiceLineItemsView/cells/DescriptionCell.module.scss";
import { AccrualImporterContext } from "./accrualImporter.context";
import {
    getAccrualViewRowLineItem,
    resolveAccrualRowExtraction,
} from "./accrualImporter.helpers";
import { AccrualImporterViewRow } from "./accrualImporter.types";
import styles from "./AccrualDocumentImporter.module.scss";

type LineItemCellProps = CellContext<
    ViewTableFeatures,
    AccrualImporterViewRow,
    unknown
>;

function useDraftLineItem(row: AccrualImporterViewRow) {
    const { mode } = useContext(AccrualImporterContext);
    return getAccrualViewRowLineItem(row, mode);
}

export const AccrualImporterItemCell = memo(({ row }: LineItemCellProps) => {
    const lineItem = useDraftLineItem(row.original);
    const itemName = lineItem?.item?.trim();

    return (
        <ViewTableCell className={itemCellStyles.item}>
            {itemName ?? null}
        </ViewTableCell>
    );
});

export const AccrualImporterDescriptionCell = memo(
    ({ row }: LineItemCellProps) => {
        const lineItem = useDraftLineItem(row.original);
        const description = lineItem?.description?.trim();

        return (
            <ViewTableCell className={descriptionCellStyles.description}>
                {description ?? null}
            </ViewTableCell>
        );
    },
);

function useDraftLineEntityId(row: AccrualImporterViewRow) {
    const { mode } = useContext(AccrualImporterContext);
    const financialDocument = row.financialDocument;
    if (!financialDocument) {
        return undefined;
    }
    return (
        financialDocument.entityId ??
        resolveAccrualRowExtraction(financialDocument, mode, row.extractionId)
            ?.entityId ??
        undefined
    );
}

function AccrualImporterAccountName({
    entityId,
    accountId,
}: {
    entityId?: number;
    accountId?: string | null;
}) {
    const showAccountNumbers = useShowAccountNumbers();
    const { accountsById } = useAccounts({
        entityId,
        includeDisabled: true,
        includeCurrentAccount: true,
        currentAccountId: accountId ?? undefined,
    });
    const account = accountId ? accountsById[accountId] : undefined;

    return (
        <ViewTableCell>
            {account ? (
                displayAccountLabel(account, showAccountNumbers)
            ) : (
                <span className={styles.placeholder}>-</span>
            )}
        </ViewTableCell>
    );
}

export const AccrualImporterAccrualAccountCell = memo(
    ({ row }: LineItemCellProps) => {
        const lineItem = useDraftLineItem(row.original);
        const entityId = useDraftLineEntityId(row.original);
        return (
            <AccrualImporterAccountName
                entityId={entityId}
                accountId={lineItem?.accountId}
            />
        );
    },
);

export const AccrualImporterBalanceAccountCell = memo(
    ({ row }: LineItemCellProps) => {
        const lineItem = useDraftLineItem(row.original);
        const entityId = useDraftLineEntityId(row.original);
        return (
            <AccrualImporterAccountName
                entityId={entityId}
                accountId={lineItem?.accrualAccountId}
            />
        );
    },
);
